import React, { useEffect, useState, createElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Copy, Check, AlertTriangle, ExternalLink, Info } from 'lucide-react';
import { formConfigsApi, signupFormsApi, emailTemplatesApi, SignupFormRecord, EmailTemplateRecord, FormConfigRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { refreshFormEmbedCodes } from './SignupFormEditor';
// --- Helpers ---
function extractLookupId(val: any): string {
  if (!val) return '';
  if (typeof val === 'object' && val.ID) return String(val.ID);
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;
  return '';
}
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  return new Promise((resolve, reject) => {
    document.execCommand('copy') ? resolve() : reject();
    textarea.remove();
  });
}
export function FormConfiguration() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // Derive team from workspace scope — strip "Team" suffix
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  // Data Lists
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  // Selection State
  const [selectedFormId, setSelectedFormId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // Content for Inspector
  const [selectedFormHtml, setSelectedFormHtml] = useState('');
  const [selectedTemplateHtml, setSelectedTemplateHtml] = useState('');
  // Conditional Rules State
  interface EmailRoutingRule {
    id: string;
    field: string;
    operator: 'equals' | 'not_equals' | 'contains';
    value: string;
    templateId: string;
  }
  const [rules, setRules] = useState<EmailRoutingRule[]>([]);
  // Confirmation page language (admin choice, baked into embed codes)
  const [confirmationLanguage, setConfirmationLanguage] = useState('');
  // UI State
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalFormId, setOriginalFormId] = useState(''); // To detect changes in edit mode
  // Initial Data Load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [formsData, templatesData] = await Promise.all([signupFormsApi.list(), emailTemplatesApi.list()]);
        // Filter forms and templates by team
        const teamFilteredForms = formsData.filter(f => f.Team === currentWorkspace.name || f.Team === team);
        const teamFilteredTemplates = templatesData.filter(t => t.Team === currentWorkspace.name || t.Team === team);
        setForms(teamFilteredForms);
        setTemplates(teamFilteredTemplates);
        // If editing, fetch the config record
        if (isEditing && id) {
          const config = await formConfigsApi.get(id);
          if (config) {
            const formId = extractLookupId(config.Sign_Up_Form);
            const templateId = extractLookupId(config.Email_Template);
            setSelectedFormId(formId);
            setOriginalFormId(formId);
            setSelectedTemplateId(templateId);
          } else {
            toast.error('Configuration not found');
            navigate('/configurations');
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load configuration data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEditing, navigate]);
  // Fetch Form HTML when selection changes
  useEffect(() => {
    if (!selectedFormId) {
      setSelectedFormHtml('');
      setRules([]);
      return;
    }
    // Always fetch the full record to ensure we have the latest Builder_Schema
    signupFormsApi.get(selectedFormId).then(record => {
      if (record) {
        setSelectedFormHtml(record.HTML || '');
        if (record.Builder_Schema) {
          try {
            const schema = JSON.parse(record.Builder_Schema);
            if (schema.emailRoutingRules && Array.isArray(schema.emailRoutingRules)) {
              setRules(schema.emailRoutingRules);
            } else {
              setRules([]);
            }
            setConfirmationLanguage(schema.confirmationLanguage || '');
          } catch (e) {
            setRules([]);
            setConfirmationLanguage('');
          }
        } else {
          setRules([]);
          setConfirmationLanguage('');
        }
      }
    }).catch(err => console.error('Failed to fetch form details', err));
  }, [selectedFormId]);
  // Fetch Template HTML when selection changes
  useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplateHtml('');
      return;
    }
    // Check if we already have the full record in the list
    const templateInList = templates.find(t => t.ID === selectedTemplateId);
    if (templateInList && templateInList.Email_HTML_Body) {
      setSelectedTemplateHtml(templateInList.Email_HTML_Body);
    } else {
      // Fetch full record if needed
      emailTemplatesApi.get(selectedTemplateId).then(record => {
        if (record) setSelectedTemplateHtml(record.Email_HTML_Body || '');
      }).catch(err => console.error('Failed to fetch template HTML', err));
    }
  }, [selectedTemplateId, templates]);
  const handleSave = async () => {
    if (!selectedFormId) {
      toast.error('Please select a form');
      return;
    }
    setIsSaving(true);
    try {
      // Check if this form already has a configuration with a DIFFERENT template (only if a template is selected)
      if (selectedTemplateId) {
        const existingConfigs = await formConfigsApi.findByFormId(selectedFormId);
        const conflicting = existingConfigs.filter(c => {
          // Skip the current config if editing
          if (isEditing && id && c.ID === id) return false;
          const linkedTemplateId = extractLookupId(c.Email_Template);
          return linkedTemplateId && linkedTemplateId !== selectedTemplateId;
        });
        if (conflicting.length > 0) {
          // Find the name of the already-linked template for a helpful message
          let linkedTemplateName = 'another template';
          const linkedId = extractLookupId(conflicting[0].Email_Template);
          const linkedTemplate = templates.find(t => t.ID === linkedId);
          if (linkedTemplate) {
            linkedTemplateName = `"${linkedTemplate.Email_Subject}"`;
          }
          toast.error('This form is already linked to ' + linkedTemplateName, {
            description: 'A form can only be linked to one email template. Remove the existing configuration first, or select a different form.',
            duration: 8000
          });
          setIsSaving(false);
          return;
        }
      }
      const payload: any = {
        Sign_Up_Form: selectedFormId,
        Team: team
      };
      // Only include Email_Template if one is selected
      if (selectedTemplateId) {
        payload.Email_Template = selectedTemplateId;
      }
      if (isEditing && id) {
        await formConfigsApi.update(id, payload);
      } else {
        await formConfigsApi.create(payload);
      }
      // Also save the conditional rules to the SignupFormRecord's Builder_Schema
      if (rules.length > 0) {
        try {
          const formRecord = await signupFormsApi.get(selectedFormId);
          if (formRecord) {
            let schema: any = {};
            if (formRecord.Builder_Schema) {
              try {
                schema = JSON.parse(formRecord.Builder_Schema);
              } catch (e) {
                // ignore parse error
              }
            }
            schema.emailRoutingRules = rules;
            if (confirmationLanguage) {
              schema.confirmationLanguage = confirmationLanguage;
            } else {
              delete schema.confirmationLanguage;
            }
            const newSchema = JSON.stringify(schema);
            console.log('[FormConfig] Saving Builder_Schema with routing rules:', {
              formId: selectedFormId,
              rulesCount: rules.length,
              schemaLength: newSchema.length,
              rules: rules.map(r => ({
                field: r.field,
                op: r.operator,
                value: r.value,
                templateId: r.templateId
              }))
            });
            await signupFormsApi.update(selectedFormId, {
              Builder_Schema: newSchema
            });
            // ── VERIFY the save actually persisted ──
            try {
              const verifyRecord = await signupFormsApi.get(selectedFormId);
              if (verifyRecord?.Builder_Schema) {
                const savedSchema = JSON.parse(verifyRecord.Builder_Schema);
                if (savedSchema.emailRoutingRules && savedSchema.emailRoutingRules.length === rules.length) {
                  console.log('[FormConfig] ✅ Routing rules verified in Builder_Schema');
                  toast.success(`${rules.length} routing rule(s) saved to form`, {
                    duration: 3000
                  });
                } else {
                  const savedCount = savedSchema.emailRoutingRules?.length || 0;
                  console.error('[FormConfig] ❌ Rule count mismatch!', {
                    expected: rules.length,
                    saved: savedCount,
                    schemaLength: verifyRecord.Builder_Schema.length
                  });
                  toast.error(`Routing rules may not have saved correctly (expected ${rules.length}, found ${savedCount})`, {
                    description: 'The Builder_Schema field in Zoho Creator may have a character limit. Check the console for details.',
                    duration: 10000
                  });
                }
              } else {
                console.error('[FormConfig] ❌ Builder_Schema is empty after save!');
                toast.error('Builder_Schema appears empty after save — rules may have been lost', {
                  duration: 8000
                });
              }
            } catch (verifyErr) {
              console.warn('[FormConfig] Could not verify save:', verifyErr);
            }
          }
        } catch (err) {
          console.error('Failed to save routing rules to form:', err);
          toast.error('Warning: Failed to save conditional routing rules', {
            description: err instanceof Error ? err.message : 'Unknown error',
            duration: 8000
          });
        }
      } else {
        // No rules — clear any existing rules from Builder_Schema, but preserve confirmationLanguage
        try {
          const formRecord = await signupFormsApi.get(selectedFormId);
          let schema: any = {};
          if (formRecord?.Builder_Schema) {
            try {
              schema = JSON.parse(formRecord.Builder_Schema);
            } catch {
              /* ignore */
            }
          }
          let changed = false;
          if (schema.emailRoutingRules) {
            delete schema.emailRoutingRules;
            changed = true;
          }
          if (confirmationLanguage) {
            if (schema.confirmationLanguage !== confirmationLanguage) changed = true;
            schema.confirmationLanguage = confirmationLanguage;
          } else if (schema.confirmationLanguage) {
            delete schema.confirmationLanguage;
            changed = true;
          }
          if (changed) {
            await signupFormsApi.update(selectedFormId, {
              Builder_Schema: JSON.stringify(schema)
            });
          }
        } catch {
          /* ignore */
        }
      }
      // ── Refresh embed codes so deployed forms include the latest rules ──
      try {
        toast.loading('Refreshing embed codes...', {
          id: 'embed-refresh'
        });
        const refreshed = await refreshFormEmbedCodes(selectedFormId);
        toast.dismiss('embed-refresh');
        if (refreshed) {
          toast.success('Embed codes updated with routing rules', {
            description: 'The deployed form will now use the latest conditional email logic.',
            duration: 5000
          });
        } else {
          toast.warning('Could not refresh embed codes automatically', {
            description: 'Open the form editor and re-save to update embed codes.',
            duration: 8000
          });
        }
      } catch (err) {
        toast.dismiss('embed-refresh');
        console.error('[FormConfig] Embed refresh failed:', err);
        toast.warning('Embed codes not refreshed', {
          description: 'Open the form editor and re-save to update embed codes with the new rules.',
          duration: 8000
        });
      }
      toast.success(isEditing ? 'Configuration updated successfully' : 'Configuration created successfully');
      navigate('/configurations');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save configuration', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleCopyVariable = (name: string) => {
    const variable = `{{${name}}}`;
    copyToClipboard(variable).then(() => toast.success(`Copied ${variable}`)).catch(() => toast.error('Failed to copy'));
  };
  // --- Render Helpers ---
  const formFields = selectedFormHtml ? extractFormFields(selectedFormHtml) : [];
  const hasInspector = selectedFormId && selectedTemplateId;
  const isVariableInTemplate = (varName: string) => {
    if (!selectedTemplateHtml) return false;
    return selectedTemplateHtml.includes(`{{${varName}}}`);
  };
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4324" data-mp="2n8g0j2zz33o6">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4325" data-mp="3jvuk390xbgqf" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4326" data-mp="3fuip0nza63au">
          Loading configuration...
        </span>
      </div>;
  }
  return <div className="space-y-6 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4327" data-mp="3b2hi7hkvt4s7">
      {/* Header */}
      <div className="flex items-center gap-4" data-id="element-4328" data-mp="364fz4wngh5gq">
        <Link to="/configurations" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4329" data-mp="7ep8i94y8rts">
          <ArrowLeft className="h-5 w-5" data-id="element-4330" data-mp="2nz716zn994d" />
        </Link>
        <div data-id="element-4331" data-mp="2me8756ar40p">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4332" data-mp="lkr8n8xalpht">
            {isEditing ? 'Edit Configuration' : 'New Configuration'}
          </h1>
          <p className="text-sm text-gray-500" data-id="element-4333" data-mp="qj2hbgbuptyg">
            Link a signup form to an email template and verify variables.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-4334" data-mp="gt95ce7srcto">
        {/* Left Column: Form Fields */}
        <div className="lg:col-span-1 space-y-6" data-id="element-4335" data-mp="2syehb6hly8i">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-4336" data-mp="2wcisoypohil">
            {/* Form Selection */}
            <div className="space-y-2" data-id="element-4337" data-mp="3pz7g74t8ihnq">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4338" data-mp="3q0a89g2amq58">
                Sign Up Form <span className="text-red-500" data-id="element-4339" data-mp="3u12jgeuq7545">*</span>
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={selectedFormId} onChange={e => setSelectedFormId(e.target.value)} data-id="element-4340" data-mp="1o72tc7qleajv">
                <option value="" data-id="element-4341" data-mp="38pn41scs6ftd">Select a form...</option>
                {forms.map(form => <option key={form.ID} value={form.ID} data-id="element-4342" data-mp="3mgi4cg97rolc">
                    {form.Form_Name}
                  </option>)}
              </select>
              <p className="text-xs text-gray-500" data-id="element-4343" data-mp="3hoqn4wby6v7v">
                The form that triggers this configuration.
              </p>
            </div>

            {/* Change Warning */}
            {isEditing && selectedFormId !== originalFormId && <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2" data-id="element-4344" data-mp="9arua3k9vpyl">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4345" data-mp="4beygtbfb8jv" />
                <div className="text-xs text-amber-800" data-id="element-4346" data-mp="2b1moiqytzh37">
                  <span className="font-medium" data-id="element-4347" data-mp="2b24u4e7dv7ns">Form changed.</span> Check the
                  variable inspector to ensure the template supports the new
                  form's fields.
                </div>
              </div>}

            {/* Template Selection */}
            <div className="space-y-2" data-id="element-4348" data-mp="1p7e4nk4w8ec8">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4349" data-mp="1p62yxerw0wih">
                Default Email Template{' '}
                <span className="text-gray-400 font-normal" data-id="element-4350" data-mp="1yxza9rizij4n">(Optional)</span>
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} data-id="element-4351" data-mp="1289hkru60be1">
                <option value="" data-id="element-4352" data-mp="1bb0vqzefdl9d">None (No confirmation email)</option>
                {templates.map(template => <option key={template.ID} value={template.ID} data-id="element-4353" data-mp="2mo3arq0vm2rf">
                    {template.Email_Subject}
                    {templates.filter(t => t.Email_Subject === template.Email_Subject).length > 1 ? ` (ID: ${template.ID})` : ''}
                  </option>)}
              </select>
              <p className="text-xs text-gray-500" data-id="element-4354" data-mp="69hv219ihrjx">
                The fallback template used if no conditional rules match.
              </p>
            </div>

            {/* Conditional Email Rules */}
            <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-4355" data-mp="3tf0eafrl3f3d">
              <div className="flex items-center justify-between" data-id="element-4356" data-mp="3tc4gqsjnesfg">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4357" data-mp="3odt64flj4dw6">
                  Conditional Email Routing
                </label>
                <button type="button" onClick={() => {
                setRules([...rules, {
                  id: Date.now().toString(),
                  field: formFields[0] || '',
                  operator: 'equals',
                  value: '',
                  templateId: ''
                }]);
              }} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-4358" data-mp="3eh829xl8x8tx">
                  + Add Rule
                </button>
              </div>
              <p className="text-xs text-gray-500" data-id="element-4359" data-mp="1s88a9w995y0b">
                Send different templates based on form field values. Rules are
                evaluated in order.
              </p>

              {rules.length > 0 && <div className="space-y-3" data-id="element-4360" data-mp="1i9iuc90x2nmp">
                  {rules.map((rule, index) => <div key={rule.id} className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-3 relative" data-id="element-4361" data-mp="1dc0qgx1raylk" data-mpi={index}>
                      <button type="button" onClick={() => setRules(rules.filter(r => r.id !== rule.id))} className="absolute top-2 right-2 text-gray-400 hover:text-red-500" data-id="element-4362" data-mp="37p8eo5v88by8">
                        &times;
                      </button>
                      <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-4363" data-mp="z79z6bhptyvi">
                        Rule {index + 1}
                      </div>

                      <div className="grid grid-cols-3 gap-2" data-id="element-4364" data-mp="6hjgrq91watl">
                        <div className="col-span-1" data-id="element-4365" data-mp="6j4dnmiu85um">
                          <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4366" data-mp="bg4ftmckgogg">
                            Field
                          </label>
                          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.field} onChange={e => {
                      const newRules = [...rules];
                      newRules[index].field = e.target.value;
                      setRules(newRules);
                    }} data-id="element-4367" data-mp="fgws6ogqj9gy">
                            <option value="" data-id="element-4368" data-mp="3o03ohvm8nz2x">Select field...</option>
                            {formFields.map(f => <option key={f} value={f} data-id="element-4369" data-mp="tkuu8uvyyfd">
                                {f}
                              </option>)}
                          </select>
                        </div>
                        <div className="col-span-1" data-id="element-4370" data-mp="34atcospm5ya3">
                          <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4371" data-mp="34871mj3il0n4">
                            Condition
                          </label>
                          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.operator} onChange={e => {
                      const newRules = [...rules];
                      newRules[index].operator = e.target.value as any;
                      setRules(newRules);
                    }} data-id="element-4372" data-mp="3e76bg500n2bp">
                            <option value="equals" data-id="element-4373" data-mp="s2o0oth1y0ez">Equals</option>
                            <option value="not_equals" data-id="element-4374" data-mp="w3gbvs9hifdw">Not Equals</option>
                            <option value="contains" data-id="element-4375" data-mp="1js91z83fdt0l">Contains</option>
                          </select>
                        </div>
                        <div className="col-span-1" data-id="element-4376" data-mp="1opsp6xi3pg4o">
                          <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4377" data-mp="1arb4l8nhsjqz">
                            Value
                          </label>
                          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.value} onChange={e => {
                      const newRules = [...rules];
                      newRules[index].value = e.target.value;
                      setRules(newRules);
                    }} placeholder="e.g. Male" data-id="element-4378" data-mp="1fp2ujx4ktvsj" />
                        </div>
                      </div>

                      <div data-id="element-4379" data-mp="12jhszujfrepy">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4380" data-mp="12iowdme6y5lj">
                          Send Template
                        </label>
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.templateId} onChange={e => {
                    const newRules = [...rules];
                    newRules[index].templateId = e.target.value;
                    setRules(newRules);
                  }} data-id="element-4381" data-mp="tj1smtxzonpb">
                          <option value="" data-id="element-4382" data-mp="1tr9nnu9tu6of">Select template...</option>
                          {templates.map(template => <option key={template.ID} value={template.ID} data-id="element-4383" data-mp="1xsldo0wm4tx5">
                              {template.Email_Subject}
                              {templates.filter(t => t.Email_Subject === template.Email_Subject).length > 1 ? ` (ID: ${template.ID})` : ''}
                            </option>)}
                        </select>
                      </div>
                    </div>)}
                </div>}
            </div>

            {/* Confirmation Page Language */}
            <div className="space-y-2 pt-4 border-t border-gray-200" data-id="element-4384" data-mp="r5g528pveu27">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4385" data-mp="1uf2dcaa2bs7w">
                Confirmation Page Language
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={confirmationLanguage} onChange={e => setConfirmationLanguage(e.target.value)} data-id="element-4386" data-mp="1phj09dp3plhj">
                <option value="" data-id="element-4387" data-mp="2dd4xobro10zd">English (Default)</option>
                <option value="pt-BR" data-id="element-4388" data-mp="28f3fdt22d5oy">🇧🇷 Português (Brasil)</option>
                <option value="es" data-id="element-4389" data-mp="28etnfzxgkogm">🇪🇸 Español</option>
                <option value="fr" data-id="element-4390" data-mp="2n49bh85lufsm">🇫🇷 Français</option>
                <option value="de" data-id="element-4391" data-mp="2n62n71v2rq7h">🇩🇪 Deutsch</option>
                <option value="it" data-id="element-4392" data-mp="2i5y3l2yvefd1">🇮🇹 Italiano</option>
                <option value="ar" data-id="element-4393" data-mp="2w4nz4e61k6za">🇸🇦 العربية</option>
                <option value="tr" data-id="element-4394" data-mp="2w57htx3imzed">🇹🇷 Türkçe</option>
                <option value="nl" data-id="element-4395" data-mp="2r5uwxhw7g2ef">🇳🇱 Nederlands</option>
                <option value="pl" data-id="element-4396" data-mp="2r7ecf6a1e9f5">🇵🇱 Polski</option>
                <option value="ru" data-id="element-4397" data-mp="3ar3gfelhphaj">🇷🇺 Русский</option>
                <option value="zh" data-id="element-4398" data-mp="3apac45ycn95p">🇨🇳 中文</option>
                <option value="ja" data-id="element-4399" data-mp="3fpeqnqfp5tta">🇯🇵 日本語</option>
                <option value="ko" data-id="element-4400" data-mp="3fo3m3oefgi14">🇰🇷 한국어</option>
                <option value="hi" data-id="element-4401" data-mp="31pn8ddi5dr9h">🇮🇳 हिन्दी</option>
                <option value="th" data-id="element-4402" data-mp="35sa2jl2ajo9u">🇹🇭 ไทย</option>
                <option value="vi" data-id="element-4403" data-mp="35qywfeldi4fk">🇻🇳 Tiếng Việt</option>
                <option value="id" data-id="element-4404" data-mp="2s159j1tfhu8h">🇮🇩 Bahasa Indonesia</option>
                <option value="ms" data-id="element-4405" data-mp="2ryshtwxvc5i8">🇲🇾 Bahasa Melayu</option>
                <option value="tl" data-id="element-4406" data-mp="2wsn9pnnmnqse">🇵🇭 Tagalog</option>
              </select>
              <p className="text-xs text-gray-500" data-id="element-4407" data-mp="2i37loffhdzge">
                The language shown on the subscription confirmation page when
                subscribers click the email link.
              </p>
            </div>

            {/* Team Field */}
            <div className="space-y-2" data-id="element-4408" data-mp="292sz7cqs6ebb">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4409" data-mp="2e0ugpt8k65ko">
                Team
              </label>
              <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4410" data-mp="2tjdmegln09mw" />
              <p className="text-xs text-gray-400" data-id="element-4411" data-mp="2jn0t3ju07xso">
                Automatically set from your current workspace scope
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4" data-id="element-4412" data-mp="2alkl1iqnw7rm">
              <button onClick={handleSave} disabled={isSaving || !selectedFormId} className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4413" data-mp="25tt2nvhnt8ck">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4414" data-mp="1rvunwozff9wj" /> : <Save className="h-4 w-4" data-id="element-4415" data-mp="1mvgsc6f0hsfe" />}
                {isSaving ? 'Saving...' : isEditing ? 'Update Configuration' : 'Create Configuration'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Variable Inspector */}
        <div className="lg:col-span-2" data-id="element-4416" data-mp="gcns4ck4slf5">
          {hasInspector ? <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4417" data-mp="3frj3g5safjc1">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-4418" data-mp="3jt4c1exshbgf">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-4419" data-mp="3osh5iij61grz">
                  <Info className="h-4 w-4 text-blue-500" data-id="element-4420" data-mp="3tinuijhlb3ar" />
                  Variable Inspector
                </h3>
                {selectedTemplateId && <Link to={`/templates/${selectedTemplateId}`} target="_blank" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-4421" data-mp="2w39zkyhdtowp">
                    Edit Template <ExternalLink className="h-3 w-3" data-id="element-4422" data-mp="2fsc1xu9w8n4x" />
                  </Link>}
              </div>

              <div className="p-6 space-y-8" data-id="element-4423" data-mp="21qznxoeamvlo">
                {/* System Variables */}
                <div className="space-y-3" data-id="element-4424" data-mp="26qclp46pdn31">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4425" data-mp="26pazq4o5fg6j">
                    System Variables
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4426" data-mp="1wt7tamm9kvds">
                    {['confirmation_link', 'team_name'].map(sysVar => {
                  const found = isVariableInTemplate(sysVar);
                  return <button key={sysVar} onClick={() => handleCopyVariable(sysVar)} className={`flex items-center justify-between p-3 rounded-md border text-left transition-all ${found ? 'bg-green-50 border-green-200 hover:border-green-300' : 'bg-amber-50 border-amber-200 hover:border-amber-300'}`} data-id="element-4427" data-mp="1n127jumlva71">
                          <div className="flex items-center gap-2" data-id="element-4428" data-mp="pjy8a0y7x0br">
                            <code className={`text-sm font-medium ${found ? 'text-green-700' : 'text-amber-700'}`} data-id="element-4429" data-mp="14gfhalixq2v5">
                              {`{{${sysVar}}}`}
                            </code>
                          </div>
                          {found ? <div className="flex items-center gap-1 text-xs text-green-600 font-medium" data-id="element-4430" data-mp="18eljdg8tvu9q">
                              <Check className="h-3 w-3" data-id="element-4431" data-mp="1i695r2qj2l4c" /> Found
                            </div> : <div className="flex items-center gap-1 text-xs text-amber-600 font-medium" data-id="element-4432" data-mp="1w58wcfxlpa35">
                              <AlertTriangle className="h-3 w-3" data-id="element-4433" data-mp="1s5gj3px9ifrg" /> Missing
                            </div>}
                        </button>;
                })}
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-4434" data-mp="246zogl3n28cj">
                    <span className="font-medium" data-id="element-4435" data-mp="295jbypxvrs70">Note:</span>{' '}
                    <code data-id="element-4436" data-mp="2dwj8vv702a9i">{`{{confirmation_link}}`}</code> is required for
                    double opt-in.
                  </p>
                </div>

                {/* Form Variables */}
                <div className="space-y-3" data-id="element-4437" data-mp="2i0h805wbrl2k">
                  <div className="flex items-center justify-between" data-id="element-4438" data-mp="2mwen68vuqldr">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4439" data-mp="2rwk9d9jcrfog">
                      Form Variables
                    </h4>
                    <span className="text-xs text-gray-400" data-id="element-4440" data-mp="2wum3qqm8b1f8">
                      {formFields.length} fields found in form
                    </span>
                  </div>

                  {formFields.length === 0 ? <div className="text-sm text-gray-500 italic bg-gray-50 p-4 rounded border border-dashed border-gray-200" data-id="element-4441" data-mp="12j7bo35qwpwb">
                      No input fields found in the selected form's HTML.
                    </div> : <div className="flex flex-wrap gap-2" data-id="element-4442" data-mp="370djh8q1964v">
                      {formFields.map(field => {
                  const found = isVariableInTemplate(field);
                  return <button key={field} onClick={() => handleCopyVariable(field)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${found ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`} title={found ? 'Found in template' : 'Not used in template (click to copy)'} data-id="element-4443" data-mp="2kkjck2d2fxi">
                            <code data-id="element-4444" data-mp="5tu53mopdp0w">{field}</code>
                            {found && <Check className="h-3 w-3" data-id="element-4445" data-mp="5vf4bplypw53" />}
                          </button>;
                })}
                    </div>}
                  <p className="text-xs text-gray-500" data-id="element-4446" data-mp="3t4k3z7kk548r">
                    Green variables are currently used in the selected template.
                    Gray ones are available but unused.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-4447" data-mp="atgjhzg9pbba">
                <p className="text-xs text-gray-500 flex items-start gap-2" data-id="element-4448" data-mp="fri44otcdiov">
                  <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-4449" data-mp="ftl3w0kaqq6s" />
                  If you need to add variables to the template, click "Edit
                  Template" above. The variables will work automatically once
                  added to the HTML body.
                </p>
              </div>
            </div> : <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center p-8 bg-gray-50 border border-gray-200 border-dashed rounded-lg" data-id="element-4450" data-mp="2nybvkwxvzlin">
              <div className="bg-white p-3 rounded-full shadow-sm mb-4" data-id="element-4451" data-mp="2x0bczqctcrm4">
                <Info className="h-6 w-6 text-gray-400" data-id="element-4452" data-mp="2wy8o59ned4iz" />
              </div>
              <h3 className="text-sm font-medium text-gray-900" data-id="element-4453" data-mp="29b0md1p6gdlp">
                Variable Inspector
              </h3>
              <p className="text-sm text-gray-500 mt-1 max-w-xs" data-id="element-4454" data-mp="256suwwrc4jjs">
                Select both a form and a template to see which variables are
                being used and which are missing. Email templates are optional.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}